import { inject, Injectable } from '@angular/core';
import { AmplifyService } from '@app/core/services/amplify.service';
import { ThingShadow } from '@cco/model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { merge, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
  getThingShadow,
  getThingShadowFailure,
  getThingShadows,
  getThingShadowsFailure,
  upsertThingShadow,
} from './iot-thing.actions';
import { notNullish } from '@app/shared/rxjs/operators/standard-operators';
import { userTenantFeature } from '@core/user-tenant/user-tenant.reducer';
import { concatLatestFrom } from '@ngrx/operators';

function shadowTopics(
  tenantId: string,
  assetType: string,
  assetUuid: string,
  thingName: string,
): { request: string; response: string } {
  const base = `ccov1-shadows/${ tenantId }/${ assetType }/${ assetUuid }/${ thingName }`;
  return { request: `${ base }/get`, response: `${ base }/get/accepted` };
}

@Injectable()
export class IotThingEffects {
  private actions$ = inject(Actions);
  private amplifyService = inject(AmplifyService);

  private readonly store = inject(Store);
  private readonly tenantId$ = this.store.select(userTenantFeature.selectSelectedTenantId).pipe(
    notNullish(),
  );

  private getShadow$(
    tenantId: string,
    assetType: string,
    assetUuid: string,
    thingName: string,
  ): Observable<Action> {
    const topics = shadowTopics(tenantId, assetType, assetUuid, thingName);
    return this.amplifyService
      // -eslint-disable-next-line @typescript-eslint/ban-types
      .request$<ThingShadow<object>>(topics.response, topics.request)
      .pipe(map(({ message }) => upsertThingShadow({ thingName, shadow: message })));
  }

  getThingShadow$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getThingShadow),
      concatLatestFrom(() => this.tenantId$),
      mergeMap(([ action, tenantId ]) =>
        this.getShadow$(tenantId, action.assetType, action.assetUuid, action.thingName).pipe(
          catchError(error =>
            of(getThingShadowFailure({
              requestId: action.requestId,
              thingName: action.thingName,
              error,
            }))),
        ),
      ),
    );
  });

  getThingShadows$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getThingShadows),
      concatLatestFrom(() => this.tenantId$),
      mergeMap(([ action, tenantId ]) =>
        // Each thing resolves on its own, so one silent thing cannot hold up the rest.
        merge(...action.thingNames.map(thingName =>
          this.getShadow$(tenantId, action.assetType, action.assetUuid, thingName).pipe(
            catchError(error =>
              of(getThingShadowsFailure({
                requestId: action.requestId,
                thingNames: [ thingName ],
                error,
              }))),
          ))),
      ),
    );
  });
}
